import { atom } from 'ximple/atoms';
import { LocalStorageKey, getDynamicStorageKey } from '../localstorage';
import { VERSION } from '@repo/widget-env/__autogen/env';
import { dispatchWidgetEvent } from 'src/events/eventDispatcher';
import { CartItem } from '@repo/types';
import { CartEvents, reduceCartState } from './cart.reducer';
import { decycle, retrocycle } from '@repo/common-utils/cycle';
import { dayjsReplacer, dayjsReviver } from '@repo/common-utils/serialize';
import { localeAtom } from '@repo/i18n';
import { debugLog, errorLog } from '@repo/common-utils/Logger';
import { flow, sortBy } from 'lodash-es';
import {
    removeInvalidCartItems,
    getCartItemId,
    filterWaitingListItems,
    filterBookableItems,
} from '@repo/widget-utils/cart/cartUtils';
import { verifyQuantities } from '@repo/widget-utils/booking/bookingHelpers';
import { displaySnackbarAtom } from '../ui/globalSnackbar.atom';

type State = CartItem[];

export const cartAtom = atom<State, CartEvents>({
    initialValue: [],
    persistKey: getDynamicStorageKey(LocalStorageKey.BILBERRY_CART),
    appVersion: VERSION,
    update: flow(verifyCapacityBeforeAdding, updateCartAtom),
    transformOnSerialize: (obj) => decycle(obj, dayjsReplacer),
    transformOnDeserialize: (obj) => retrocycle(obj, dayjsReviver),
});

function verifyCapacityBeforeAdding(
    state: CartItem[],
    action: CartEvents,
): [CartItem[], CartEvents] {
    if (action.type !== 'ADD') return [state, action];
    try {
        const actionId = getCartItemId(action.data);
        const willOverwriteElement = state.some((item) => getCartItemId(item) === actionId);

        if (willOverwriteElement) {
            return [state, action];
        }

        // Check if the product is an accommodation or "night", and in so case, check the capacity without considering ticketOptions.
        if (
            action.data.products?.[0]?.product &&
            (action.data.products[0].product.type === 'accommodation' ||
                action.data.products[0].product.type === 'nights')
        ) {
            const countExisting = state.filter((item) =>
                item.products.some((p) => !!action.data.products.find((ap) => ap.id === p.id)),
            ).length;

            // For accommodation or "night" products, we don't care about the last product because we're not actually booking that one
            // It's only used for some logic that expects it to be there.
            // There is definitely a better way to handle this, but sadly, no time to refactor it right now.
            const productsSortedByStart = sortBy(action.data.products, (x) => x.start);
            const productsExceptLast = productsSortedByStart.slice(0, -1);

            const lowestCapacityInRange = Math.min(...productsExceptLast.map((p) => p.capacity));
            if (countExisting + 1 > lowestCapacityInRange) {
                displaySnackbarAtom.update({
                    message:
                        localeAtom.subject.value.t.thereIsNotEnoughCapacityForTheItemsInThisCart,
                    severity: 'error',
                    visible: true,
                });
                return [
                    state,
                    {
                        type: 'NOOP' as any,
                    },
                ];
            } else {
                return [state, action];
            }
        }

        // find all overlapping products
        const allItemsOverlapping = state.filter((item) =>
            item.products.some((p) => !!action.data.products.find((ap) => ap.id === p.id)),
        );

        // if no overlapping products, we can skip the rest
        if (allItemsOverlapping.length === 0) return [state, action];

        // combine the quantities of the ticketOptions and verify the new total quantity in the cart
        const combinedTicketOptionQuantity = action.data.ticketOptions.map((q) => {
            const allMatching = allItemsOverlapping.map((item) =>
                item.ticketOptions.find((x) => x.id === q.id),
            );
            const totalQuantity = allMatching.reduce((acc, x) => acc + (x?.quantity ?? 0), 0);
            return { ...q, quantity: q.quantity + totalQuantity };
        });

        const errors = verifyQuantities(combinedTicketOptionQuantity);
        if (errors.length > 0) {
            displaySnackbarAtom.update({
                message: localeAtom.subject.value.t.thereIsNotEnoughCapacityForTheItemsInThisCart,
                severity: 'error',
                visible: true,
            });
            return [
                state,
                {
                    type: 'NOOP' as any,
                },
            ];
        }
    } catch (e) {
        // If there is an error, log it, and continue as if nothing happened.
        errorLog('Error in verifyCapacityBeforeAdding', e);
        return [state, action];
    }

    return [state, action];
}

function updateCartAtom([state, action]: [CartItem[], CartEvents]) {
    const result = reduceCartState(state, action);

    dispatchWidgetEvents(action, state);

    return result;
}

let lastLocale = localeAtom.subject.value.locale;
localeAtom.subject.subscribe(({ locale }) => {
    if (lastLocale !== locale) {
        debugLog(`Changing locale from ${lastLocale} to ${locale}. Clearing cart.`);
        cartAtom.update({ type: 'CLEAR' });
    }
    lastLocale = locale;
});

function dispatchWidgetEvents(action: CartEvents, currentCartItems: CartItem[]) {
    switch (action.type) {
        case 'ADD':
            dispatchWidgetEvent({ eventType: 'addToCart', cartItems: [action.data] });
            break;
        case 'REMOVE':
            dispatchWidgetEvent({
                eventType: 'removeFromCart',
                cartItems: [currentCartItems[action.data as unknown as number]],
            });
            break;
    }
}

export const selectValidItems = (state: State) => removeInvalidCartItems(state);

export const selectIsProductInCart = (state: State, productId: string) =>
    selectValidItems(state).some((cartItem) => cartItem.products[0]?.product?.id === productId);

export const selectWaitingListItems = (state: State) => filterWaitingListItems(state);

export const selectRegularItems = (state: State) => filterBookableItems(state);

export type CartCompositionType = 'empty' | 'waitingListOnly' | 'regularOnly' | 'mixed';

export const selectCartComposition = (state: State): CartCompositionType => {
    if (state.length === 0) return 'empty';

    const waitingListItems = selectWaitingListItems(state);
    const regularItems = selectRegularItems(state);

    if (waitingListItems.length > 0 && regularItems.length > 0) return 'mixed';
    else if (waitingListItems.length > 0) return 'waitingListOnly';
    else if (regularItems.length > 0) return 'regularOnly';
    else return 'empty';
};
